import { LoadingArea, Step, StepLayoutChildProps, useSteps } from '@consigli/facade';
import {
  ExtendedFile,
  useGetPackagesByTypeQuery,
  useProjectId,
  useStartPipelineMutation,
  useUpdatePackageMutation,
} from '@consigli/hooks';
import { DocumentPackageType, JobType, Language, PipelineName } from '@consigli/types';
import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { useNavigationHistory } from '@/features/package-creation/use-navigation-history';
import { Nav } from '@/layouts/nav';
import { PipelineStage } from '@/pages/report-generation/utils/pipeline-helpers';
import { Route } from '@/routes';

import { ReportCreationForm } from './report-creation-form';
import { PackageFormData } from './types';
import { useFetchValidateName } from '../components/user-input';
import { useCreateInsightPackage } from '../hooks/use-create-insight-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export const ReportGenerationWizard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const projectId = useProjectId();
  const { wizardBackButtonClick } = useNavigationHistory();

  const [templateFiles, setTemplateFiles] = useState<ExtendedFile[]>([]);
  const [documentFiles, setDocumentFiles] = useState<ExtendedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  const methods = useForm<PackageFormData>({
    defaultValues: {
      packageName: '',
      language: Language.EN,
      reportType: '',
      location: '',
    },
    mode: 'onChange',
  });

  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type: DocumentPackageType.REPORT_GENERATION,
  });

  const { createDocumentPackage, uploadFiles } = useCreateInsightPackage({
    setCurrentCounter,
    setTargetCounter,
  });
  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);
  const [startPipeline] = useStartPipelineMutation();
  const [updatePackage] = useUpdatePackageMutation();

  const handleAnalyze = useCallback(
    async (packageName: string, language: Language, reportType: string, location: string) => {
      if (templateFiles.length === 0) {
        toast.error(
          t('package-creation.reports.sample-report-required', 'Please upload a sample report'),
        );
        return;
      }

      if (documentFiles.length === 0) {
        toast.error(
          t(
            'package-creation.reports.document-files-required',
            'Please upload at least one document',
          ),
        );
        return;
      }

      try {
        setIsProcessing(true);
        const packageId = await createDocumentPackage(
          DocumentPackageType.REPORT_GENERATION,
          packageName,
          language,
          false,
          undefined,
          null,
          { reportType, location },
        );

        if (!packageId) {
          toast.error(t('package-creation.package-create-failed', 'Failed to create package'));
          setIsProcessing(false);
          return;
        }

        const markedSampleReportFiles: ExtendedFile[] = templateFiles.map((file) => {
          return Object.assign(file, { isSampleReport: true });
        });
        const allFiles = [...markedSampleReportFiles, ...documentFiles];
        await uploadFiles(allFiles, packageId);
        await startPipeline({
          projectId,
          packageId,
          pipelineName: PipelineName.REPORT_TEMPLATE_CREATION,
          notify: true,
          jobType: JobType.DOCUMENT_PACKAGE,
        });
        await updatePackage({
          projectId,
          packageId,
          data: {
            data: {
              reportType,
              location,
              currentStage: PipelineStage.TEMPLATE_IN_PROGRESS,
            },
          },
          isHidden: false,
        }).unwrap();

        toast.success(
          t(
            'package-creation.reports.pipeline-started',
            'Template creation started. You will be notified when ready.',
          ),
        );

        navigate(
          `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${Route.REPORT_GENERATION}/${Route.PACKAGES}/${packageId}/${Route.RESULTS}`,
        );
      } catch (error) {
        console.error('Error creating report package:', error);
        toast.error(
          t('package-creation.reports.failed-to-create', 'Failed to create report package'),
        );
        setIsProcessing(false);
      }
    },
    [
      templateFiles,
      documentFiles,
      createDocumentPackage,
      uploadFiles,
      updatePackage,
      startPipeline,
      projectId,
      navigate,
      t,
    ],
  );

  const { withStep, StepIndicator, StepOutlet } = useSteps(
    (step: (name: string, component: FC<StepLayoutChildProps>) => Step) => {
      return [
        step(t('package-creation.reports.step-configure-upload', 'Configure & Upload'), () => (
          <ReportCreationForm
            templateFiles={templateFiles}
            setTemplateFiles={setTemplateFiles}
            documentFiles={documentFiles}
            setDocumentFiles={setDocumentFiles}
            onAnalyze={handleAnalyze}
            validateName={validateName}
          />
        )),
      ];
    },
  );

  return withStep(() => (
    <div className="w-full flex flex-col min-h-[calc(100vh-theme(space.14))] bg-day-light-4">
      <Nav projectId={projectId} handleClick={wizardBackButtonClick} />
      <div className="flex flex-col flex-1 items-center px-4 py-6 w-full mx-auto">
        {isUploading || isProcessing ? (
          <LoadingArea
            className="mt-20"
            title={t('document-upload.uploading')}
            message={progress < 1 ? t('document-upload.initialising') : `${progress}%`}
          />
        ) : (
          <>
            <div className="mb-4">
              <h1 className="text-3xl font-semibold text-day-neutral-dark text-center">
                {t('package-creation.reports.title', 'Create Report')}
              </h1>
            </div>
            <div className="w-full flex flex-col items-center">
              <div className="w-full max-w-4xl mb-4">
                <StepIndicator />
              </div>
              <div className="w-full max-w-4xl bg-white rounded-lg shadow-sm p-8">
                <FormProvider {...methods}>
                  <StepOutlet />
                </FormProvider>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ));
};
